
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import 'lodash/isEqual';
import 'just-kebab-case';
import { l as labelsMixin, k as keyCodes } from './labels-mixin.js';
import debounce from 'lodash/debounce';
import DOMPurify from 'dompurify';
import throttle from 'lodash/throttle';
import { e as emitter } from './emitter-mixin.js';

function loadScript() {
  if (window.zxcvbn) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/zxcvbn/4.4.2/zxcvbn.js';
    script.integrity = 'sha256-Znf8FdJF85f1LV0JmPOob5qudSrns8pLPZ6qkd/+F0o=';
    script.crossOrigin = 'anonymous';
    script.onload = resolve;
    script.onerror = reject;
    document.getElementsByTagName('head')[0].appendChild(script);
  });
}

function checkPasswordStrength(password) {
  return loadScript().then(() => {
    if (password) {
      return window.zxcvbn(password);
    }

    return 0;
  });
}

const DEFAULT_DURATION = 300;
var script = {
  name: 'PendoInput',
  mixins: [emitter, labelsMixin],
  inject: {
    elForm: {
      default: ''
    },
    elFormItem: {
      default: ''
    }
  },
  inheritAttrs: false,
  componentName: 'ElInput',
  props: {
    value: {
      type: null,
      default: null
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: null
    },
    sanitize: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'medium'
    },
    type: {
      type: String,
      default: 'text'
    },
    width: {
      type: String,
      default: '100%'
    },
    debounce: {
      type: [Boolean, Number],
      default: false
    },
    throttle: {
      type: [Boolean, Number],
      default: false
    },
    resize: {
      type: [String, Boolean],
      default: false
    },
    autosize: {
      type: Boolean,
      default: true
    },
    showPasswordStrength: {
      type: Boolean,
      default: false
    },
    invalid: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      passwordStrength: null,
      focused: false,
      lazyValue: this.value
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.lazyValue;
      },

      set(val) {
        this.lazyValue = val;
        this.$emit('input', this.lazyValue);
      }

    },

    passwordStrengthLabel() {
      if (!this.internalValue) {
        return;
      }

      return {
        0: 'Very weak',
        1: 'Weak',
        2: 'So-so',
        3: 'Good',
        4: 'Great'
      }[this.passwordStrength];
    },

    actualWidth() {
      const width = this.width;

      switch (width) {
        case 'medium':
          return '232px';

        case 'small':
          return '160px';

        case 'mini':
          return '80px';
      }

      return width;
    },

    delayDuration() {
      if (this.debounce) {
        return typeof this.debounce !== typeof true ? Number(this.debounce) : DEFAULT_DURATION;
      }

      return typeof this.throttle !== typeof true ? Number(this.throttle) : DEFAULT_DURATION;
    }

  },
  watch: {
    async internalValue() {
      if (this.showPasswordStrength) {
        this.passwordStrength = await this.checkPasswordStrength();
      }
    },

    passwordStrength(score) {
      this.$emit('score', score);
    },

    value(val) {
      this.lazyValue = val;
      this.dispatch('ElFormItem', 'el.form.change', [val]);
    }

  },

  created() {
    if (this.debounce || this.throttle) {
      const strategies = {
        throttle,
        debounce
      };
      const strategy = this.debounce ? 'debounce' : 'throttle';
      this.onInput = strategies[strategy](this.onInput, this.delayDuration);
    }
  },

  beforeDestroy() {
    if (this.type === 'textarea' && !this.resize && this.autosize) {
      this.$refs.textarea.removeEventListener('input', this.resizeTextarea);
    }
  },

  async mounted() {
    if (this.autofocus) {
      this.focus();
    }

    if (this.type === 'textarea' && !this.resize && this.autosize) {
      await this.$nextTick();
      this.$refs.textarea.addEventListener('input', this.resizeTextarea);
      this.$refs.textarea.style.height = `${this.$refs.textarea.scrollHeight + 2}px`;
    }
  },

  methods: {
    focus() {
      this.onFocus();
    },

    blur() {
      requestAnimationFrame(() => {
        const ref = this.type === 'textarea' ? 'textarea' : 'input';

        if (this.$refs[ref]) {
          this.$refs[ref].blur();
        }
      });
    },

    onChange(event) {
      if (this.onInput.flush) {
        this.onInput.flush(event.target.value);
      }

      this.updateValue(event.target.value);
      this.$emit('change', this.internalValue);
      this.$emit('onChange', this.internalValue);
    },

    onKeydown(event) {
      if (event.keyCode === keyCodes.enter) {
        this.onChange(event);
      }

      if (event.keyCode === keyCodes.esc) {
        this.blur();
      }

      this.$emit('keydown', event);
    },

    onFocus(event) {
      const ref = this.type === 'textarea' ? 'textarea' : 'input';

      if (document.activeElement !== this.$refs[ref]) {
        return this.$refs[ref].focus();
      }

      if (!this.focused) {
        this.focused = true;

        if (event) {
          this.$emit('focus', event);
        }
      }
    },

    async onBlur(event) {
      this.focused = false;

      if (event) {
        await this.$nextTick();
        this.$emit('blur', event);
        this.dispatch('ElFormItem', 'el.form.blur', [this.internalValue]);
      }
    },

    onInput(event) {
      this.updateValue(event.target.value);
    },

    updateValue(value) {
      if (this.sanitize) {
        value = DOMPurify.sanitize(value);
      }

      this.internalValue = value;
    },

    resizeTextarea(event) {
      this.$refs.textarea.style.height = 'auto';
      this.$refs.textarea.style.height = `${event.target.scrollHeight + 2}px`;
    },

    async checkPasswordStrength() {
      const _await$checkPasswordS = await checkPasswordStrength(this.internalValue),
            score = _await$checkPasswordS.score;

      return score;
    }

  }
};

const __vue_script__ = script;

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "pendo-input",
    class: ["pendo-input--" + _vm.size, {
      'pendo-input--file': _vm.type === 'file',
      'pendo-input--textarea': _vm.type === 'textarea',
      'pendo-input--prefix': _vm.$slots.prefix,
      'pendo-input--suffix': _vm.$slots.suffix,
      'pendo-input--prepend': _vm.$slots.prepend,
      'pendo-input--append': _vm.$slots.append,
      'pendo-input--password-strength': _vm.showPasswordStrength,
      'pendo-input--resizable': _vm.resize,
      'is-inline-label': !!_vm.inlineLabel,
      'is-invalid': _vm.invalid,
      'is-disabled': _vm.disabled
    }],
    style: {
      width: _vm.actualWidth
    },
    attrs: {
      "data-strength": _vm.passwordStrength
    }
  }, [_vm.hasTopLabel ? _c('div', {
    staticClass: "pendo-input__label pendo-input__label--top"
  }, [_vm._t("topLabel", [_vm._v("\n            " + _vm._s(_vm.topLabel) + "\n        ")])], 2) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "pendo-input__control"
  }, [_vm.type !== 'textarea' ? [_vm.$slots.prepend ? _c('div', {
    staticClass: "pendo-input__prepend"
  }, [_vm._t("prepend")], 2) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "pendo-input__field",
    class: {
      'is-focused': _vm.focused
    },
    on: {
      "click": _vm.focus
    }
  }, [_vm.$slots.prefix ? _c('div', {
    staticClass: "pendo-input__prefix"
  }, [_vm._t("prefix")], 2) : _vm._e(), _vm._v(" "), _c('input', _vm._b({
    ref: "input",
    attrs: {
      "tabindex": "0",
      "disabled": _vm.disabled,
      "placeholder": _vm.placeholder,
      "autofocus": _vm.autofocus,
      "type": _vm.type
    },
    domProps: {
      "value": _vm.lazyValue
    },
    on: {
      "blur": _vm.onBlur,
      "focus": _vm.onFocus,
      "change": _vm.onChange,
      "input": _vm.onInput,
      "keydown": _vm.onKeydown
    }
  }, 'input', _vm.$attrs, false)), _vm._v(" "), _vm.$slots.suffix ? _c('div', {
    staticClass: "pendo-input__suffix"
  }, [_vm._t("suffix")], 2) : _vm._e(), _vm._v(" "), _vm.showPasswordStrength ? _c('div', {
    staticClass: "pendo-input__suffix"
  }, [_c('transition', {
    attrs: {
      "name": "password-strength-label"
    }
  }, [_vm.passwordStrengthLabel ? _c('div', {
    staticClass: "password-strength__label"
  }, [_vm._v("\n                            " + _vm._s(_vm.passwordStrengthLabel) + "\n                        ")]) : _vm._e()])], 1) : _vm._e()]), _vm._v(" "), _vm.$slots.append ? _c('div', {
    staticClass: "pendo-input__append"
  }, [_vm._t("append")], 2) : _vm._e()] : _vm._e(), _vm._v(" "), _vm.type === 'textarea' ? _c('div', {
    ref: "textareaContainer",
    staticClass: "pendo-input__field",
    class: {
      'is-focused': _vm.focused
    },
    style: {
      resize: _vm.resize ? _vm.resize : 'none'
    }
  }, [_c('textarea', _vm._b({
    ref: "textarea",
    attrs: {
      "tabindex": "0",
      "disabled": _vm.disabled,
      "placeholder": _vm.placeholder,
      "autofocus": _vm.autofocus
    },
    domProps: {
      "value": _vm.lazyValue
    },
    on: {
      "input": _vm.onInput,
      "focus": _vm.onFocus,
      "blur": _vm.onBlur
    }
  }, 'textarea', _vm.$attrs, false))]) : _vm._e()], 2), _vm._v(" "), _vm.showPasswordStrength ? _c('div', {
    staticClass: "pendo-input__password-strength"
  }, [_c('div', {
    staticClass: "password-strength__background"
  }), _vm._v(" "), _c('div', {
    staticClass: "password-strength__meter"
  }), _vm._v(" "), _c('div', {
    staticClass: "password-strength__meter-divider password-strength__meter-divider--25"
  }), _vm._v(" "), _c('div', {
    staticClass: "password-strength__meter-divider password-strength__meter-divider--50"
  }), _vm._v(" "), _c('div', {
    staticClass: "password-strength__meter-divider password-strength__meter-divider--75"
  })]) : _vm._e(), _vm._v(" "), _vm.hasBottomLabel ? _c('div', {
    staticClass: "pendo-input__label pendo-input__label--bottom"
  }, [_vm._t("bottomLabel", [_vm._v("\n            " + _vm._s(_vm.bottomLabel) + "\n        ")])], 2) : _vm._e(), _vm._v(" "), _vm.$slots['helper-text'] ? _c('div', {
    staticClass: "pendo-input__helper-text"
  }, [_vm._t("helper-text")], 2) : _vm._e()]);
};

var __vue_staticRenderFns__ = [];
const __vue_inject_styles__ = undefined;
const __vue_scope_id__ = undefined;
const __vue_is_functional_template__ = false;

function __vue_normalize__(template, style, script, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  const component = (typeof script === 'function' ? script.options : script) || {};
  component.__file = "pendo-input.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}

var PendoInput = __vue_normalize__({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__);

export default PendoInput;
